/* global AbortController, HTMLElement, window */

export class ThreeDMaterialsExplorer extends HTMLElement {
  connectedCallback() {
    this.disconnectedCallback()
    this.abortController = new AbortController()
    this.tabs = [...this.querySelectorAll('[data-material-tab]')]
    this.tablist = this.querySelector('.switch-navbar')
    this.panels = [...this.querySelectorAll('[data-material-panel]')]
    this.products = [...this.querySelectorAll('[data-material-product]')]
    this.productCarousels = [...this.querySelectorAll('[data-material-product-carousel]')]
    this.mobileCarousel = this.querySelector('[data-material-mobile-carousel]')
    this.variantToggles = [...this.querySelectorAll('[data-material-variant-toggle]')]
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.selectedGroupIndex = null
    this.resizeFrame = 0
    this.productResizeFrame = 0

    for (const tab of this.tabs) {
      tab.addEventListener('click', this.handleTabClick, { signal: this.abortController.signal })
      tab.addEventListener('keydown', this.handleTabKeydown, {
        signal: this.abortController.signal,
      })
    }
    for (const option of this.querySelectorAll('[data-material-variant]')) {
      option.addEventListener('click', this.handleVariantClick, {
        signal: this.abortController.signal,
      })
    }
    for (const toggle of this.variantToggles) {
      toggle.addEventListener('click', this.handleVariantToggle, {
        signal: this.abortController.signal,
      })
    }
    for (const carousel of this.productCarousels) {
      carousel.addEventListener('scroll', this.handleProductScroll, {
        passive: true,
        signal: this.abortController.signal,
      })
    }
    this.mobileCarousel?.addEventListener('carousel:change', this.handleMobileCarouselChange, {
      signal: this.abortController.signal,
    })
    for (const eventName of ['control:prev', 'control:next']) {
      this.addEventListener(eventName, this.handleCarouselControl, {
        capture: true,
        signal: this.abortController.signal,
      })
    }
    this.addEventListener('shopify:block:select', this.handleBlockSelect, {
      signal: this.abortController.signal,
    })
    this.addEventListener('keydown', this.handleKeydown, { signal: this.abortController.signal })
    window.addEventListener('resize', this.handleResize, { signal: this.abortController.signal })
    this.selectGroup(this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'))
  }

  disconnectedCallback() {
    this.abortController?.abort()
    window.cancelAnimationFrame?.(this.resizeFrame)
    window.cancelAnimationFrame?.(this.productResizeFrame)
    this.abortController = null
    this.tabs = []
    this.tablist = null
    this.panels = []
    this.products = []
    this.productCarousels = []
    this.mobileCarousel = null
    this.variantToggles = []
    this.reducedMotion = null
    this.selectedGroupIndex = null
    this.resizeFrame = 0
    this.productResizeFrame = 0
  }

  handleTabClick = (event) => this.selectGroup(this.tabs.indexOf(event.currentTarget))

  handleTabKeydown = (event) => {
    const current = this.tabs.indexOf(event.currentTarget)
    if (current < 0) return
    let next = current
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown')
      next = (current + 1) % this.tabs.length
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
      next = (current - 1 + this.tabs.length) % this.tabs.length
    if (event.key === 'Home') next = 0
    if (event.key === 'End') next = this.tabs.length - 1
    if (next === current) return
    event.preventDefault()
    this.tabs[next]?.focus?.()
    this.selectGroup(next)
  }

  handleVariantClick = (event) => {
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') return
    const product = event.currentTarget.closest?.('[data-material-product]')
    if (product) this.selectVariant(product, event.currentTarget)
  }

  handleVariantToggle = (event) => {
    const variants = event.currentTarget.closest?.('.product-variants')
    if (!variants) return
    const open = !variants.classList.contains('active')
    this.closeVariantMenus()
    variants.classList.toggle('active', open)
    event.currentTarget.setAttribute('aria-expanded', String(open))
  }

  handleKeydown = (event) => {
    if (event.key !== 'Escape') return
    const open = this.querySelector('.product-variants.active')
    if (!open) return
    event.preventDefault()
    this.closeVariantMenus()
    open.querySelector('[data-material-variant-toggle]')?.focus?.()
  }

  handleProductScroll = (event) => this.syncProductPagination(event.currentTarget)

  handleMobileCarouselChange = (event) => {
    const groupIndex = Number(event.detail?.slide?.dataset.groupIndex)
    if (!Number.isInteger(groupIndex) || groupIndex === this.selectedGroupIndex) return
    this.selectGroup(groupIndex, { syncMobile: false })
  }

  handleCarouselControl = (event) => {
    if (!this.reducedMotion?.matches) return
    const carousel = event.target?.closest?.(
      '[data-material-mobile-carousel], [data-material-product-carousel]',
    )
    if (!carousel || !this.contains(carousel)) return
    event.preventDefault()
    event.stopImmediatePropagation()
    if (event.type === 'control:prev') carousel.previous?.(false)
    else carousel.next?.(false)
  }

  handleBlockSelect = (event) => {
    const product = this.products.find(
      (candidate) => candidate.dataset.shopifyEditorBlock === event.detail?.blockId,
    )
    if (product) this.selectGroup(this.products.indexOf(product))
  }

  handleResize = () => {
    this.queueProductPosition()
    window.cancelAnimationFrame?.(this.resizeFrame)
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = 0
        this.syncMobilePosition()
      })
    })
  }

  syncMobilePosition() {
    if (!this.mobileCarousel?.offsetParent || this.selectedGroupIndex === null) return
    this.mobileCarousel.select?.(this.selectedGroupIndex * 3, {
      animate: false,
      force: true,
    })
  }

  queueProductPosition() {
    window.cancelAnimationFrame?.(this.productResizeFrame)
    // Hidden material products need one layout/ResizeObserver cycle before native selection.
    this.productResizeFrame = window.requestAnimationFrame(() => {
      this.productResizeFrame = window.requestAnimationFrame(() => {
        this.productResizeFrame = 0
        for (const carousel of this.productCarousels) {
          if (!carousel.offsetParent) continue
          carousel.select?.(carousel.selectedIndex, { animate: false, force: true })
          this.syncProductPagination(carousel)
        }
      })
    })
  }

  selectGroup(index, { syncMobile = true } = {}) {
    if (!this.tabs.length) return
    const selected = Math.min(this.tabs.length - 1, index >= 0 ? index : 0)
    this.selectedGroupIndex = selected
    this.tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === selected
      tab.classList.toggle('active', active)
      tab.setAttribute('aria-selected', String(active))
      tab.tabIndex = active ? 0 : -1
    })
    this.panels.forEach((panel, panelIndex) => {
      const active = panelIndex === selected
      panel.classList.toggle('active', active)
      panel.hidden = !active
    })
    this.products.forEach((product, productIndex) => {
      const active = productIndex === selected
      product.classList.toggle('active', active)
      product.hidden = !active
    })
    this.closeVariantMenus()
    this.queueProductPosition()
    this.syncProductPagination(
      this.products[selected]?.querySelector('[data-material-product-carousel]'),
    )
    this.revealTab(selected)
    if (syncMobile)
      this.mobileCarousel?.select?.(selected * 3, { animate: !this.reducedMotion?.matches })
  }

  revealTab(index) {
    const tab = this.tabs[index]
    if (!tab || !this.tablist) return
    const inset = 24
    const start = tab.offsetLeft
    const end = start + tab.offsetWidth
    let left = this.tablist.scrollLeft
    if (start < left + inset) left = Math.max(0, start - inset)
    else if (end > left + this.tablist.clientWidth - inset)
      left = end - this.tablist.clientWidth + inset
    this.tablist.scrollTo?.({
      left,
      behavior: this.reducedMotion?.matches ? 'auto' : 'smooth',
    })
  }

  closeVariantMenus() {
    for (const variants of this.querySelectorAll('.product-variants.active')) {
      variants.classList.remove('active')
      variants
        .querySelector('[data-material-variant-toggle]')
        ?.setAttribute('aria-expanded', 'false')
    }
  }

  syncProductPagination(carousel) {
    if (!carousel) return
    const product = carousel.closest?.('[data-material-product]')
    const current = product?.querySelector('[data-material-image-current]')
    const slides = [...carousel.querySelectorAll('.swiper-slide')]
    if (!current || !slides.length) return

    const slideWidth = carousel.clientWidth || slides[0].getBoundingClientRect?.().width || 1
    const index = Math.max(
      0,
      Math.min(slides.length - 1, Math.round((carousel.scrollLeft || 0) / slideWidth)),
    )
    current.textContent = String(index + 1)
  }

  selectVariant(product, option) {
    for (const candidate of product.querySelectorAll('[data-material-variant]')) {
      const active = candidate === option
      candidate.classList.toggle('active', active)
      candidate.setAttribute('aria-selected', String(active))
    }
    const id = option.dataset.id || ''
    const available = option.dataset.available === 'true'
    const input = product.querySelector('[data-material-variant-input]')
    const submit = product.querySelector('[data-material-atc]')
    const buyNow = product.querySelector('[data-material-buy-now]')
    const currentVariant = product.querySelector('[data-material-current-variant]')
    const hasDiscount = Boolean(option.dataset.discount)

    if (input) input.value = id
    if (submit) submit.disabled = !available
    for (const price of product.querySelectorAll('[data-material-price]'))
      price.textContent = option.dataset.price || ''
    for (const compare of product.querySelectorAll('[data-material-compare]')) {
      compare.textContent = option.dataset.compare || ''
      compare.hidden = !hasDiscount
    }
    for (const discount of product.querySelectorAll('[data-material-discount]'))
      discount.textContent = option.dataset.discount || ''
    for (const container of product.querySelectorAll('[data-material-discount-container]'))
      container.hidden = !hasDiscount
    for (const row of product.querySelectorAll('[data-material-discount-row]'))
      row.hidden = !hasDiscount
    if (currentVariant)
      currentVariant.textContent = option.querySelector('.pr-title')?.textContent?.trim() || ''
    this.closeVariantMenus()

    if (buyNow) {
      if (available && id && buyNow.tagName === 'A') {
        const cartUrl = (this.dataset.cartUrl || '/cart').replace(/\/$/u, '')
        buyNow.href = cartUrl + '/' + id + ':1'
        buyNow.removeAttribute('aria-disabled')
      } else {
        buyNow.removeAttribute?.('href')
        buyNow.setAttribute('aria-disabled', 'true')
      }
    }

    const imageId = option.dataset.imageId
    if (imageId) {
      const image = product.querySelector('[data-image-id="' + imageId + '"]')
      const carousel = image?.closest?.('[data-material-product-carousel]')
      const imageIndex = [...(carousel?.querySelectorAll('.swiper-slide') || [])].indexOf(image)
      if (imageIndex >= 0) carousel?.select?.(imageIndex, { animate: !this.reducedMotion?.matches })
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('lp-three-d-materials')) {
  globalThis.customElements.define('lp-three-d-materials', ThreeDMaterialsExplorer)
}
